import React from 'react';
import cn from 'classnames';
import { bbCodeToHtml } from '../../../utils/formUtils';
import { Visibility, Pagination, FileProps } from '../../../types/common-types';
import RadioItem from '../radioItem';
import './style.scss';
import Button, { ButtonType } from '../button';
import Icon from '../icon';

export interface TableRowGroup {
  value: string;
  title: string;
  buttonTitle?: string;
}
export interface TableExtra {
  page?: string;
  totalPages?: string;
  groups?: TableRowGroup[];
  groupButtonTitle?: string;
}

export enum TableType {
  Radio = 'radio',
  // eslint-disable-next-line no-shadow
  Group = 'group',
}
export interface TableCell {
  value: string;
  title?: string;
  file?: FileProps;
  buttonTitle?: string;
  visibility?: Visibility;
}
export interface TableRow {
  value?: string;
  groupValue?: string;
  options: TableCell[];
}

interface Props {
  id?: string;
  submiting?: boolean;
  head: TableCell[];
  body: TableRow[];
  type?: TableType;
  error?: boolean;
  value?: string;
  styleName?: string;
  extra?: TableExtra;
  required?: boolean;
  helperText?: string;
  visibility?: Visibility;
  onChange: (value: { id: string; value: string; error: boolean }) => void;
  onClick: (id: string, buttonData?: Record<string, unknown>) => void;
  onFileClick?: (file: FileProps) => void;
}

const Table: React.FC<Props> = ({
  id = '',
  type,
  head,
  body = [],
  visibility,
  submiting = false,
  error = false,
  value,
  extra = {},
  styleName,
  helperText,
  onChange,
  onClick: onSubmit,
  onFileClick,
}) => {
  const { page, totalPages, groups = [] } = extra;
  const visibleHead = head.filter(
    cell => cell.visibility !== Visibility.Hidden
  );
  const visibleBody = body.map(row => {
    return {
      ...row,
      options: row.options.filter(r =>
        visibleHead.find(h => h.value === r.value)
      ),
    };
  });
  const header = { value, options: visibleHead };
  const withRadio = type === TableType.Radio;
  const handleChange = (val: string) => {
    if (visibility === Visibility.Disabled) return;
    onChange({ id, value: val, error: false });
  };
  const onClickActionHandler = (action: string, val?: string) => {
    onSubmit(id, { action, page, value: val });
  };
  const renderFile = (file: FileProps) => {
    const { mimeType } = file;
    if (mimeType.indexOf('image') !== -1) {
      return (
        <div styleName="table__img">
          <img src={file.path} alt={file.name} />
        </div>
      );
    }
    return (
      <div styleName="table__img">
        <Icon value="pdf" />
      </div>
    );
  };

  const renderCell = (cell: TableCell, rowValue = '') => {
    const { title, value: cellValue, file, buttonTitle } = cell;
    if (file) {
      return (
        // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
        <td
          key={cellValue}
          onKeyPress={() => {
            if (onFileClick) onFileClick(file);
          }}
          onClick={() => {
            if (onFileClick) onFileClick(file);
          }}
        >
          <div styleName="table__img">{renderFile(file)}</div>
        </td>
      );
    }
    if (buttonTitle) {
      return (
        <td>
          <Button
            id={`${id}-${rowValue}-button`}
            type={ButtonType.Normal}
            label={buttonTitle}
            styleName="table__pagination__button"
            onClick={() => onClickActionHandler('button', rowValue)}
          />
        </td>
      );
    }
    return (
      <td
        key={cellValue}
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
          __html: bbCodeToHtml(title),
        }}
      />
    );
  };
  const renderHeader = (row: TableRow) => {
    const { value: rowValue, options: rowCells } = row;
    return (
      <tr key={rowValue}>
        {withRadio ? <th>&nbsp;</th> : null}
        {rowCells.map(cell => {
          const { title, value: cellValue } = cell;
          return (
            // eslint-disable-next-line jsx-a11y/control-has-associated-label
            <th
              key={cellValue}
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{
                __html: bbCodeToHtml(title),
              }}
            />
          );
        })}
      </tr>
    );
  };

  const renderRow = (row: TableRow) => {
    const { value: rowValue = '', options: rowCells } = row;

    return (
      <tr
        key={rowValue}
        styleName={cn('table__row', { active: value === rowValue })}
      >
        {withRadio ? (
          <td>
            <RadioItem
              value={rowValue}
              activeItem={value}
              onClick={handleChange}
            />
          </td>
        ) : null}
        {rowCells.map(cell => renderCell(cell, rowValue))}
      </tr>
    );
  };

  const renderBody = (rows: TableRow[]) => {
    if (type !== TableType.Group) {
      return rows.map(row => renderRow(row));
    }
    const groupedRows: { [key: string]: TableRow[] } = {};
    groups.forEach(k => {
      groupedRows[k.value] = [];
    });
    rows.forEach(row => {
      if (row.groupValue) groupedRows[row.groupValue].push(row);
    });
    return Object.keys(groupedRows).map(key => {
      const findGroup = groups.find(g => g.value === key);
      if (!findGroup) return;
      const title = findGroup ? findGroup.title : '';
      const colspan = head.length;
      const result = groupedRows[key].map(row => renderRow(row));
      if (findGroup.buttonTitle) {
        result.push(
          <tr styleName={cn('table__row')} key={`group-${key}-controls`}>
            <td colSpan={colspan}>
              <Button
                type={ButtonType.Normal}
                label={findGroup.buttonTitle}
                styleName="table__row__group__button"
                onClick={() => {
                  onClickActionHandler(TableType.Group, findGroup.value);
                }}
              />
            </td>
          </tr>
        );
      }
      result.unshift(
        <tr styleName={cn('table__row__group')} key={`group-${key}`}>
          <th colSpan={colspan}>{title}</th>
        </tr>
      );
      // eslint-disable-next-line consistent-return
      return result;
    });
  };

  const renderPagination = () => {
    return (
      <div data-cy-prop="pagination" styleName={cn('table__pagination')}>
        <Button
          id={`${id}-${Pagination.First}`}
          type={ButtonType.Normal}
          label="<<"
          styleName="table__pagination__button"
          onClick={() => onClickActionHandler(Pagination.First)}
        />
        <Button
          id={`${id}-${Pagination.Prev}`}
          type={ButtonType.Normal}
          label="<"
          styleName="table__pagination__button"
          onClick={() => onClickActionHandler(Pagination.Prev)}
        />
        <span styleName="table__pagination__current">{page}</span>
        <Button
          id={`${id}-${Pagination.Next}`}
          type={ButtonType.Normal}
          label=">"
          styleName="table__pagination__button"
          onClick={() => onClickActionHandler(Pagination.Next)}
        />
        <Button
          id={`${id}-${Pagination.Last}`}
          type={ButtonType.Normal}
          label=">>"
          styleName="table__pagination__button"
          onClick={() => onClickActionHandler(Pagination.Last)}
        />
      </div>
    );
  };
  const tableHeader = renderHeader(header);
  const tableBody = renderBody(visibleBody);
  const withPagination = page && totalPages && totalPages !== '1';
  return (
    <div
      id={id}
      data-cy="table"
      className={styleName}
      styleName={cn('table', {
        hidden: visibility === Visibility.Hidden,
        error,
      })}
    >
      <div styleName={cn('table__wrap')}>
        <table>
          <thead>{tableHeader}</thead>
          <tbody>{tableBody}</tbody>
        </table>
      </div>
      {helperText && (
        <div data-cy-prop="errorMsg" styleName={cn('table__helperText')}>
          {helperText}
        </div>
      )}
      {withPagination ? renderPagination() : null}
      {submiting ? (
        <>
          <div styleName="table__spinner__background" />
          <div styleName="table__spinner__wrap">
            <div styleName="table__spinner">
              <div styleName="bar1" />
              <div styleName="bar2" />
              <div styleName="bar3" />
              <div styleName="bar4" />
              <div styleName="bar5" />
              <div styleName="bar6" />
              <div styleName="bar7" />
              <div styleName="bar8" />
              <div styleName="bar9" />
              <div styleName="bar10" />
              <div styleName="bar11" />
              <div styleName="bar12" />
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
};
export default Table;
